import { logger } from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClipboardCheck, Search, Download, Trash2, Menu, LogOut, Home, Users, Trophy, Calendar, TrendingUp, BarChart3, Newspaper, Bot } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AdminNav from '../components/AdminNav';

const getScoreColorClass = (percentual) => {
  if (percentual >= 70) return 'text-green-400';
  if (percentual >= 50) return 'text-yellow-400';
  return 'text-red-400';
};

const AdminQuiz = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [attempts, setAttempts] = useState([]);
  const [filteredAttempts, setFilteredAttempts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        await axios.get(`${BACKEND_URL}/api/admin/verify`, { withCredentials: true });
        localStorage.removeItem('admin_token');
        fetchData();
      } catch (error) {
        logger.error('Sessão inválida:', error);
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    };

    verifyAuth();
    // eslint-disable-next-line
  }, [navigate]);

  useEffect(() => {
    filterAttempts();
    // eslint-disable-next-line
  }, [searchTerm, filterEstado, attempts]);

  const fetchData = async () => {
    try {
      const [attemptsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/quiz/attempts?limit=1000`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/quiz/stats`, { withCredentials: true })
      ]);
      setAttempts(attemptsRes.data);
      setFilteredAttempts(attemptsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      logger.error('Erro ao buscar dados:', error);
      toast({
        title: "Erro ao carregar dados",
        description: "Não foi possível carregar as informações do quiz.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filterAttempts = () => {
    let filtered = attempts;

    if (filterEstado !== 'all') {
      filtered = filtered.filter(a => a.experiencia === filterEstado);
    }

    if (searchTerm) {
      filtered = filtered.filter(a =>
        a.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.whatsapp.includes(searchTerm)
      );
    }

    setFilteredAttempts(filtered);
  };

  const handleDelete = async (attemptId) => {
    if (!window.confirm('Tem certeza que deseja deletar esta tentativa?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/quiz/attempts/${attemptId}`, { withCredentials: true });
      toast({
        title: "Tentativa deletada",
        description: "Registro removido com sucesso.",
      });
      fetchData();
    } catch (error) {
      logger.error('Erro ao deletar:', error);
      toast({
        title: "Erro ao deletar",
        description: "Não foi possível deletar o registro.",
        variant: "destructive"
      });
    }
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Nome', 'Email', 'WhatsApp', 'Experiência', 'Mensagem', 'Pontuação', 'Percentual', 'Completado', 'Data'],
      ...filteredAttempts.map(a => [
        a.nome,
        a.email,
        a.whatsapp,
        a.experiencia || '',
        (a.mensagem || '').replace(/[\n,]/g, ' '),
        `${a.pontuacao}/${a.total_perguntas}`,
        `${a.percentual?.toFixed(0) || 0}%`,
        a.completado ? 'Sim' : 'Não',
        new Date(a.data_inicio).toLocaleDateString('pt-BR')
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `quiz_resultados_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    toast({
      title: "Exportação concluída",
      description: `${filteredAttempts.length} registros exportados.`,
    });
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout`, {}, { withCredentials: true });
    } catch (e) { /* ignore */ }
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const uniqueEstados = [...new Set(attempts.map(a => a.experiencia).filter(Boolean))].sort();

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 border-r border-gray-800 transition-all duration-300`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-8">
            {sidebarOpen && (
              <h1 className="text-xl font-bold text-yellow-400">WING ADMIN</h1>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-gray-400 hover:text-white p-2"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>

          <AdminNav active="quiz" navigate={navigate} sidebarOpen={sidebarOpen} onLogout={handleLogout} />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Gestão do Quiz</h1>
            <p className="text-gray-400">{filteredAttempts.length} participantes</p>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="glass-dark p-6 rounded-xl border border-yellow-500/20">
                <div className="flex items-center justify-between mb-4">
                  <ClipboardCheck className="w-8 h-8 text-yellow-400" />
                  <span className="text-xs text-gray-400">TOTAL</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stats.total_tentativas}</div>
                <div className="text-sm text-gray-400">Total de Tentativas</div>
              </div>

              <div className="glass-dark p-6 rounded-xl border border-green-500/20">
                <div className="flex items-center justify-between mb-4">
                  <Trophy className="w-8 h-8 text-green-400" />
                  <span className="text-xs text-gray-400">COMPLETAS</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stats.tentativas_completas}</div>
                <div className="text-sm text-gray-400">Quiz Finalizados</div>
              </div>

              <div className="glass-dark p-6 rounded-xl border border-blue-500/20">
                <div className="flex items-center justify-between mb-4">
                  <TrendingUp className="w-8 h-8 text-blue-400" />
                  <span className="text-xs text-gray-400">MÉDIA</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stats.media_pontuacao}/10</div>
                <div className="text-sm text-gray-400">Pontuação Média</div>
              </div>

              <div className="glass-dark p-6 rounded-xl border border-purple-500/20">
                <div className="flex items-center justify-between mb-4">
                  <Calendar className="w-8 h-8 text-purple-400" />
                  <span className="text-xs text-gray-400">MÊS</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stats.tentativas_este_mes}</div>
                <div className="text-sm text-gray-400">Este Mês</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="glass-dark p-4 rounded-xl border border-white/10 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nome, email ou WhatsApp..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                  data-testid="quiz-search-input"
                />
              </div>

              <select
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-yellow-500"
                data-testid="quiz-filter-experiencia"
              >
                <option value="all" className="bg-gray-900">Todas Experiências</option>
                {uniqueEstados.map(exp => (
                  <option key={exp} value={exp} className="bg-gray-900 capitalize">{exp}</option>
                ))}
              </select>

              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                data-testid="quiz-export-btn"
              >
                <Download className="w-5 h-5" />
                Exportar CSV
              </button>
            </div>
          </div>

          {/* Results Table */}
          <div className="glass-dark rounded-xl border border-white/10 overflow-hidden">
            {filteredAttempts.length === 0 ? (
              <p className="text-gray-400 text-center py-12">Nenhuma tentativa encontrada.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800 bg-white/5">
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Nome</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Contato</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Experiência</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Pontuação</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Data</th>
                      <th className="text-center py-4 px-4 text-gray-400 font-medium text-sm">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAttempts.map((attempt) => (
                      <tr key={attempt.id} className="border-b border-gray-800 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-4">
                          <div className="text-white font-medium">{attempt.nome}</div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="text-gray-300 text-sm">{attempt.email}</div>
                          <div className="text-gray-400 text-sm">{attempt.whatsapp}</div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-sm font-medium capitalize">
                            {attempt.experiencia || '-'}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          {attempt.completado ? (
                            <div>
                              <span className={`text-lg font-bold ${getScoreColorClass(attempt.percentual)}`}>
                                {attempt.pontuacao}/10
                              </span>
                              <div className="text-xs text-gray-400">{attempt.percentual?.toFixed(0)}%</div>
                            </div>
                          ) : (
                            <span className="text-gray-500 text-sm">Não finalizado</span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-gray-400 text-sm">
                          {new Date(attempt.data_inicio).toLocaleDateString('pt-BR')}
                          <div className="text-xs text-gray-500">
                            {new Date(attempt.data_inicio).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() => handleDelete(attempt.id)}
                              className="p-2 text-red-400 hover:bg-red-500/10 rounded transition-colors"
                              title="Deletar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminQuiz;
